import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { api } from '../services/api';
import { SearchIcon, PlusIcon, PencilIcon, TrashIcon } from '../components/Icons';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  mfa_enabled: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  expires_at?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('basic');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: '',
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.get('/users');
      setUsers(resp.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = (id: string, name: string) => {
    setDeleteDialog({ open: true, id, name });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteDialog.id}`);
      setDeleteDialog({ open: false, id: '', name: '' });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const handleEdit = (user: User) => {
    setEditingId(user.id);
    setEmail(user.email);
    setFullName(user.full_name);
    setPassword('');
    setRole(user.role);
    if (user.expires_at) {
      setHasExpiry(true);
      // Format to local input datetime format (YYYY-MM-DDTHH:MM)
      const dateObj = new Date(user.expires_at);
      const tzOffset = dateObj.getTimezoneOffset() * 60000; // offset in milliseconds
      const localISOTime = (new Date(dateObj.getTime() - tzOffset)).toISOString().slice(0, 16);
      setExpiryDate(localISOTime);
    } else {
      setHasExpiry(false);
      setExpiryDate('');
    }
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setEmail('');
    setFullName('');
    setPassword('');
    setRole('basic');
    setHasExpiry(false);
    setExpiryDate('');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setEmail('');
    setFullName('');
    setPassword('');
    setRole('basic');
    setHasExpiry(false);
    setExpiryDate('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Convert selected date-time to UTC timezone
      let expiresAtUTC: string | null = null;
      if (hasExpiry && expiryDate) {
        expiresAtUTC = new Date(expiryDate).toISOString();
      }

      if (editingId) {
        // Update user
        await api.patch(`/users/${editingId}`, {
          email,
          full_name: fullName,
          role,
          password: password || undefined,
          expires_at: expiresAtUTC,
          clear_expiry: !hasExpiry,
        });
      } else {
        // Create user
        await api.post('/users', {
          email,
          full_name: fullName,
          role,
          password,
          expires_at: expiresAtUTC,
        });
      }
      setShowForm(false);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al guardar usuario');
    }
  };

  const filteredUsers = users.filter((u) => {
    const s = search.toLowerCase();
    return (
      u.full_name.toLowerCase().includes(s) ||
      u.email.toLowerCase().includes(s) ||
      u.role.toLowerCase().includes(s)
    );
  });

  const getExpiryBadge = (user: User) => {
    if (!user.expires_at) {
      return { label: 'Sin límite', style: styles.badgeGreen };
    }

    const expiryTime = new Date(user.expires_at).getTime();
    const now = Date.now();

    if (now > expiryTime) {
      return { label: 'Demo Expirada', style: styles.badgeRed };
    }

    // Active demo, calculate remaining days/hours
    const diffMs = expiryTime - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) {
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
      return { label: `Expira en ${diffHours}h`, style: styles.badgeOrange };
    }
    return { label: `Demo (${diffDays} días)`, style: styles.badgeActive };
  };

  return (
    <Layout>
      <div>
        {!showForm && (
          <>
            <h1 style={styles.pageTitle}>Gestión de Usuarios</h1>
            <p style={styles.pageSubtitle}>
              Crea y administra las cuentas de tu equipo, asigna niveles de acceso (RBAC) y gestiona los plazos de expiración de demos.
            </p>
          </>
        )}

        {showForm ? (
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>{editingId ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre Completo *</label>
                  <input
                    style={styles.input}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    placeholder="Ej: Juan Pérez"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email / Usuario *</label>
                  <input
                    type="email"
                    style={styles.input}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Ej: juan@jjagency.com"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Contraseña {editingId ? '(dejar en blanco para no cambiar)' : '*'}</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={!editingId}
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rol de Acceso *</label>
                  <select
                    style={styles.input}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="basic">Básico (Acceso limitado a sus propios Clientes)</option>
                    <option value="master">Master (Acceso total al negocio y administración)</option>
                  </select>
                </div>

                <div style={{ ...styles.formGroup, gridColumn: '1 / -1', marginTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="hasExpiry"
                      checked={hasExpiry}
                      onChange={(e) => setHasExpiry(e.target.checked)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px', accentColor: 'var(--accent)' }}
                    />
                    <label htmlFor="hasExpiry" style={{ fontSize: '0.9rem', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                      Establecer expiración de cuenta (Control de Demo)
                    </label>
                  </div>
                </div>

                {hasExpiry && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Fecha y Hora de Expiración (Zona Horaria Local) *</label>
                    <input
                      type="datetime-local"
                      style={styles.input}
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      required={hasExpiry}
                    />
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      La fecha seleccionada se convertirá y guardará automáticamente en formato UTC en el sistema.
                    </span>
                  </div>
                )}
              </div>

              <div style={styles.formActions}>
                <button type="button" onClick={handleCancel} style={styles.cancelBtn}>
                  Volver
                </button>
                <button type="submit" style={styles.saveBtn}>
                  {editingId ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div style={styles.gridHeader}>
              <div style={styles.searchBar}>
                <SearchIcon size={18} color="var(--text-muted)" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o rol..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </div>
              <div style={styles.gridActions}>
                <button onClick={handleNew} style={styles.addButton}>
                  <PlusIcon size={16} color="#fff" />
                  <span>Nuevo Usuario</span>
                </button>
              </div>
            </div>

            {error && <div style={styles.errorBanner}>{error}</div>}

            <div style={styles.tableContainer}>
              {loading ? (
                <div style={styles.loading}>Cargando usuarios...</div>
              ) : (
                <div style={styles.tableScroll}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.theadRow}>
                        <th style={styles.th}>Nombre</th>
                        <th style={styles.th}>Email</th>
                        <th style={styles.th}>Rol</th>
                        <th style={styles.th}>Expiración (Demo)</th>
                        <th style={styles.th}>Fecha de Creación</th>
                        <th style={styles.th}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => {
                        const expiryBadge = getExpiryBadge(user);
                        return (
                          <tr key={user.id} style={styles.tr}>
                            <td style={styles.td}>
                              <div style={styles.userName}>{user.full_name}</div>
                            </td>
                            <td style={styles.td}>{user.email}</td>
                            <td style={styles.td}>
                              <span style={user.role === 'master' ? styles.badgeMaster : styles.badgeBasic}>
                                {user.role === 'master' ? 'Master' : 'Básico'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={expiryBadge.style}>{expiryBadge.label}</span>
                                {user.expires_at && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {new Date(user.expires_at).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={styles.td}>
                              {new Date(user.created_at).toLocaleDateString()}
                            </td>
                            <td style={styles.td}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => handleEdit(user)}
                                  style={styles.editBtn}
                                  title="Editar"
                                >
                                  <PencilIcon size={14} color="var(--text-secondary)" />
                                </button>
                                <button
                                  onClick={() => handleDelete(user.id, user.full_name)}
                                  style={styles.deleteBtn}
                                  title="Eliminar"
                                >
                                  <TrashIcon size={14} color="var(--error)" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div style={styles.emptyState}>
                      <div style={styles.emptyText}>No se encontraron usuarios</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        title="Eliminar usuario"
        message={`¿Estás seguro de que quieres eliminar a "${deleteDialog.name}"? Esta acción invalidará todos sus accesos de forma permanente.`}
        confirmText="Eliminar permanentemente"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, id: '', name: '' })}
      />
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  pageTitle: { fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' },
  pageSubtitle: { color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' },
  sectionTitle: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' },
  card: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    padding: '2rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.25rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '0.8rem',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-input)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'var(--font-body)',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem',
    paddingTop: '1.25rem',
    borderTop: '1px solid var(--border)',
  },
  cancelBtn: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'all 0.2s',
    fontFamily: 'var(--font-body)',
  },
  saveBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'background 0.2s',
    fontFamily: 'var(--font-body)',
  },
  gridHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    minWidth: '300px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    padding: '0 0.75rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.7rem 0.5rem',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  gridActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addButton: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.25rem',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: 'var(--font-body)',
    transition: 'background 0.2s',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    color: 'var(--error)',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-sm)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    border: '1px solid rgba(220, 38, 38, 0.15)',
  },
  tableContainer: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  tableScroll: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontSize: '0.9rem',
  },
  theadRow: {
    background: 'var(--bg-hover)',
    borderBottom: '1px solid var(--border)',
  },
  th: {
    padding: '1rem 1.5rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
  },
  tr: {
    borderBottom: '1px solid var(--border)',
    transition: 'background 0.2s',
  },
  td: {
    padding: '1rem 1.5rem',
    color: 'var(--text-primary)',
    verticalAlign: 'middle',
  },
  userName: {
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  badgeMaster: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'rgba(197, 160, 89, 0.15)',
    color: '#c5a059',
    border: '1px solid rgba(197, 160, 89, 0.3)',
  },
  badgeBasic: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'var(--bg-hover)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  },
  badgeGreen: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'rgba(16, 185, 129, 0.1)',
    color: 'var(--success)',
    border: '1px solid rgba(16, 185, 129, 0.2)',
  },
  badgeRed: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'rgba(220, 38, 38, 0.1)',
    color: 'var(--error)',
    border: '1px solid rgba(220, 38, 38, 0.2)',
  },
  badgeActive: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  badgeOrange: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: 'rgba(217, 119, 6, 0.1)',
    color: 'var(--warning)',
    border: '1px solid rgba(217, 119, 6, 0.2)',
  },
  editBtn: {
    background: 'transparent',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'background 0.2s',
  },
  loading: {
    padding: '3rem',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '1rem',
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
    borderBottom: 'none',
  },
  emptyText: {
    color: 'var(--text-secondary)',
    fontSize: '0.95rem',
  },
};

export default UserManagement;
